import React, { createContext, useCallback, useState, useContext } from 'react';

import type { TextCache, TTextBoxContentItem } from '@src/types/wysiwyg_editor';
import { PageContentStateContext } from './page_content';
import { useDispatch, useSelector } from 'react-redux';
import {
  textCacheAddItem,
  textCacheRemoveItem
} from '@src/actions/wysiwyg/editor';
import { CoverDesignerStore } from '@src/types/CoverDesigner';
import { TStencilStore } from '@src/types/Stencil';

interface Props {
  children: React.ReactElement;
}

interface State {
  insertionTarget: string | null;
  texts: TextCache;
  dragging: boolean;
}

interface Dispatch {
  add: (url: TTextBoxContentItem) => void;
  remove: (uuid: string) => void;
  clearInsertionTarget: () => void;
  setInsertionTarget: (id: string) => void;
  startDragging: () => void;
  stopDragging: () => void;
}

type CacheStore = CoverDesignerStore | TStencilStore;

const defaultStateValue = {
  insertionTarget: null,
  texts: [],
  dragging: false
};
const defaultDispatchValue = {
  add: () => {},
  remove: () => {},
  clearInsertionTarget: () => {},
  setInsertionTarget: () => {},
  startDragging: () => {},
  stopDragging: () => {}
};

export const TextCacheStateContext = createContext<State>(defaultStateValue);
export const TextCacheDispatchContext = createContext<Dispatch>(
  defaultDispatchValue
);

export const TextCacheProvider = ({ children }: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const texts = useSelector((store: CacheStore) => store.cache.text);
  const [dragging, setDragging] = useState(false);
  const [insertionTarget, setInsertionTarget] = useState<string | null>(null);

  const add = useCallback(
    (item: TTextBoxContentItem) => dispatch(textCacheAddItem(item)),
    [dispatch]
  );

  const remove = useCallback(
    (uuid: string) => dispatch(textCacheRemoveItem(uuid)),
    [dispatch]
  );

  const clearInsertionTarget = useCallback(() => setInsertionTarget(null), []);

  return (
    <TextCacheDispatchContext.Provider
      value={{
        add,
        clearInsertionTarget,
        remove,
        setInsertionTarget,
        startDragging: () => setDragging(true),
        stopDragging: () => setDragging(false)
      }}
    >
      <TextCacheStateContext.Provider
        value={{
          texts,
          insertionTarget,
          dragging
        }}
      >
        {children}
      </TextCacheStateContext.Provider>
    </TextCacheDispatchContext.Provider>
  );
};
